<template>
    <div class="page-content">
        <div class="d-flex flex-column flex-md-row py-3 pl-4">
            <h1 class="text-nowrap">
                {{ invoiceName }}
            </h1>
            <!-- For debugging in CI -->
            <RedirectToApp
                v-if="invoiceDetails && invoiceDetails.encoded_id"
                :ciUrl="
                    `${env.APPS}billing/invoice/view/${invoiceDetails.encoded_id}?forceci=true` ||
                    '#'
                "
            />
            <div
                v-if="warningMessagePayment"
                class="alert alert-warning m-0 ml-md-3 mt-2 mt-md-0"
                role="alert"
            >
                {{ warningMessagePayment }}
            </div>
        </div>

        <b-card footer-class="p-0" :key="cardKey">
            <div class="">
                <loader-profile
                    v-if="!isDataLoaded || isBusinessLoading"
                    :showImage="false"
                ></loader-profile>
                <div v-else>
                    <invoice-edit
                        ref="invoiceEdit"
                        v-if="profileMode === 'EDIT'"
                        :invoice="invoiceDetails"
                        @saved="saved"
                        v-on="$listeners"
                        :tabCount="tabCount"
                    />
                    <invoice-display
                        ref="invoiceDisplay"
                        v-if="profileMode === 'VIEW'"
                        :invoice="invoiceDetails"
                    />
                </div>
            </div>

            <template
                v-slot:footer
                no-body
                v-if="firstTimeLoaded && invoiceDetails"
            >
                <b-tabs
                    card
                    lazy
                    class="list tab-table-responsive"
                    v-if="invoiceDetails"
                    v-model="activeTabIndex"
                    ref="btabs"
                >
                    <!-- For type other fee when editing the item will not show the price tier tab -->
                    <tab-items
                        :key="1 + keyComponent"
                        ref="tabItems"
                        tabname="tabItems"
                        @add-invoice-item="addInvoiceItem"
                        :invoice="invoiceDetails"
                        :tabCount="tabCount.item_count"
                        :profileMode="profileMode"
                        @reload-invoice-details="reloadPage"
                        @create-bundle="createBundle"
                        @redirect-to-dashboard="redirectToDashboard"
                    />
                    <tab-payments
                        :key="2 + keyComponent"
                        ref="tabPayments"
                        tabname="tabPayments"
                        :invoice="invoiceDetails"
                        :tabCount="tabCount.payment_count"
                        @create-payment="createPayment"
                        @reload-invoice-details="reloadPage('tabPayments')"
                    />

                    <tab-credits
                        :key="3 + keyComponent"
                        ref="tabCredits"
                        tabname="tabCredits"
                        :invoice="invoiceDetails"
                        :tabCount="tabCount.credit_count"
                        @create-credit="createCredit"
                        @reload-invoice-details="reloadPage('tabCredits')"
                    />
                    <tab-returns
                        :key="4 + keyComponent"
                        ref="tabReturns"
                        tabname="tabReturns"
                        :invoice="invoiceDetails"
                        :tabCount="tabCount.returns_count"
                    />
                    <tab-declined
                        :key="5 + keyComponent"
                        ref="tabDeclined"
                        tabname="tabDeclined"
                        :tabCount="tabCount.declined_count"
                        @reload-invoice-details="reloadPage('tabItems')"
                    />
                    <tab-write-off
                        v-if="tabCount.writeoff_count > 0"
                        :key="6 + keyComponent"
                        ref="tabWriteOff"
                        tabname="tabWriteOff"
                        :invoice="invoiceDetails"
                        :tabCount="tabCount.writeoff_count"
                        @reload-invoice-details="reloadPage('tabItems')"
                    />
                    <tab-late-fees
                        :key="7 + keyComponent"
                        ref="tabLateFees"
                        tabname="tabLateFees"
                        :invoice="invoiceDetails"
                        :tabCount="tabCount.latefee_count"
                        @reload-invoice-details="reloadPage('tabLateFees')"
                    />
                </b-tabs>
            </template>
        </b-card>

        <div v-if="invoiceDetails && invoiceDetails.client.id">
            <popup-notes :clientId="invoiceDetails.client.id" :key="popupKey" />
        </div>
        <invoice-item-modal
            v-if="invoiceDetails"
            ref="invoiceItemModal"
            @reload="reloadPage('tabItems')"
            @showVetsourceModal="openVetsourceModal"
            @redirect-to-dashboard="redirectToDashboard"
        />
        <createPayment
            v-if="invoiceDetails"
            ref="createPaymentWindow"
            :client="invoiceDetails.client"
            :invoice="invoiceDetails"
            module="invoice"
            @reload="reloadPage('tabPayments')"
        />
        <modal-edit-credit
            v-if="invoiceDetails"
            modal-id="modal-edit-credit"
            ref="modal-edit-credit"
            :invoicePage="invoiceDetails.id"
            :client-name="invoiceDetails.client"
            @reload="reloadPage('tabCredits')"
        />
        <modal-bundle
            ref="modalBundle"
            @reload="reloadPage('tabItems')"
            @redirect-to-dashboard="redirectToDashboard"
        />
        <modal-vetsource
            ref="modalVetsource"
            @reload="reloadPage('tabItems')"
        />
        <modal-switch-business
            ref="modalSwitchBusiness"
            @redirect-to-dashboard="redirectToDashboard"
        />
    </div>
</template>

<script>
/**
 * @name Invoice
 * @description invoice display, details and navbar
 * @see apps/application/views/staff/billing/invoice/invoice/sub/navbar.php
 *
 */

import unsavePrompt from "@/mixins/unsave-prompt.js";
import joinPaymentInvoiceSocket from "@/mixins/join-payment-invoice-socket.js";
import InvoiceDisplay from "@/components/invoice/invoice-display";
import InvoiceEdit from "@/components/invoice/invoice-update";
import LoaderProfile from "@/components/elements/loader-profile";
import TabItems from "@/module/billing/tabs/invoice/tab-items";
import TabPayments from "@/module/billing/tabs/invoice/tab-payments";
import TabCredits from "@/module/billing/tabs/invoice/tab-credits";
import TabReturns from "@/module/billing/tabs/invoice/tab-returns";
import TabDeclined from "@/module/billing/tabs/invoice/tab-declined";
import TabWriteOff from "@/module/billing/tabs/invoice/tab-write-offs";
import TabLateFees from "@/module/billing/tabs/invoice/tab-late-fees";
import PopupNotes from "@/components/client/popup-notes";
import CreatePayment from "@/components/new-payment/payment-modal.vue";
import InvoiceGeneralService from "@/services/invoice/invoice-general";
import ModalEditCredit from "@/components/billing-credit/credit-edit-modal";
import ModalBundle from "@/components/invoice-item/bundle-modal";
import ModalVetsource from "@/components/invoice-item/modal-vetsource";
import invoiceItemModal from "@/components/invoice-item/create-update";
import CBILLINGS from "@/constants/billings";
import { isEmpty, findIndex, assignIn, uniqBy } from "lodash";
import ModalSwitchBusiness from "@/components/commons/modal-switch-business.vue";
import RedirectToApp from "@/components/commons/redirect-to-app.vue";

const IS_JOIN_SOCKET_PAYMENT_INVOICE = true;
const TAB_COUNTS = {
    ITEM: 1,
    PAYMENT: 2,
    CREDIT: 3,
    RETURN: 4,
    DECLINE: 5,
    WRITE_OFF: 6,
    LATE_FEE: 7
};
export default {
    mixins: [unsavePrompt, joinPaymentInvoiceSocket],
    components: {
        InvoiceDisplay,
        InvoiceEdit,
        LoaderProfile,
        TabItems,
        TabPayments,
        TabCredits,
        TabReturns,
        TabDeclined,
        TabWriteOff,
        TabLateFees,
        PopupNotes,
        CreatePayment,
        ModalEditCredit,
        ModalBundle,
        invoiceItemModal,
        ModalVetsource,
        ModalSwitchBusiness,
        RedirectToApp
    },
    props: {
        profileMode: {
            type: String
        },
        invoiceDetails: {
            type: Object
        }
    },
    data() {
        return {
            TAB_COUNTS: TAB_COUNTS,
            isDataReloading: true,
            isLoading: false,
            record: {},
            keyComponent: 0,
            cardKey: 0,
            activeTabIndex: 0,
            firstTimeLoaded: false,
            tabCount: {
                item_count: 0,
                payment_count: 0,
                credit_count: 0,
                returns_count: 0,
                declined_count: 0,
                writeoff_count: 0,
                latefee_count: 0
            },
            isEditTypeFee: false
        };
    },
    computed: {
        isLocalEnv() {
            return process.env.VUE_APP_ENV === "local";
        },
        invoiceId() {
            return this.invoiceDetails ? this.invoiceDetails.id : null;
        },
        invoiceName() {
            if (!this.isDataLoaded) {
                return "Loading...";
            }
            return this.invoiceDetails ? this.invoiceDetails.name : "";
        },
        isDataLoaded() {
            return !isEmpty(this.invoiceDetails) && !this.isDataReloading;
        },
        routeParam() {
            return this.$route.params.id;
        },
        isShowTab() {
            return (
                (this.profileMode == "VIEW" && this.isTypeFee) ||
                (this.profileMode == "EDIT" && this.isEditTypeFee)
            );
        },
        popupKey() {
            return this.$store.getters["client/popupKey"];
        },
        form_dirty() {
            return this.$store.getters["billing/isDirty"];
        },
        warningMessagePayment() {
            return this.$store.getters["billing/warningMessagePayment"](
                this.invoiceId
            );
        },
        isBusinessLoading() {
            return this.$store.getters["isBusinessLoading"];
        }
    },
    watch: {
        routeParam() {
            this.$store.commit("billing/setInvoiceDetails", null);
            this.reloadPageDetails([], IS_JOIN_SOCKET_PAYMENT_INVOICE);
            this.cardKey++;
        },
        isBusinessLoading() {
            if (!this.isBusinessLoading && !this.isDataLoaded) {
                this.$emit("redirect-to-dashboard");
            }
        }
    },
    mounted() {
        this.$socket.client.on(
            "v-broadcast",
            this.handleVBroadcastPaymentSocket
        );

        // trigger message after redirect from successfully converted from estimate
        if (window.localStorage.getItem("convertedEstimate")) {
            let message = window.localStorage.getItem("convertedEstimate");
            this.$nextTick(() => {
                self._message(message);
                window.localStorage.removeItem("convertedEstimate");
            });
        }

        // fetch uom
        let self = this;
        this.$store.dispatch("references/getUOM");
        this.reloadPageDetails([], IS_JOIN_SOCKET_PAYMENT_INVOICE);

        this.$root.$on("idexx-reload", function () {
            self.reloadPage("tabItems");
        });

        this.$root.$on("reload-count", function (type) {
            self.reloadTabCount(type);
        });

        this.$root.$on("antech-ui-closed", function () {
            self.reloadPage("tabItems");
        });
    },
    destroyed() {
        this.$root.$off("reload-count");
        this.$root.$off("antech-ui-closed");
        this.$root.$off("idexx-reload");
        this.$store.dispatch("billing/clearInvoiceDetails");
        // TODO: Below code perhaps can put globally to handle disable payment or any actions related to the invoice from other module
        this.$socket.client.off(
            "v-broadcast",
            this.handleVBroadcastPaymentSocket
        );
    },
    methods: {
        handleVBroadcastPaymentSocket(payload) {
            switch (payload.action) {
                case "payment:invoice-paid":
                    this.handlePaymentInvoicePaid(payload);
                    break;

                default:
                    break;
            }
        },
        handlePaymentInvoicePaid(payload) {
            let isInvoiceIncludeInInvoiceIdsSocket = payload.invoiceIds.includes(
                this.invoiceId
            );

            if (isInvoiceIncludeInInvoiceIdsSocket) {
                this.$store.commit("billing/setIsSkipReEnableInvoice", true);
                let text = `This invoice has successfully collecting payment by ${payload.userName}. This page will be refreshed.`;
                this._message(text, "warning", true);
                setTimeout(() => {
                    location.reload();
                }, 4000);
            }
        },
        async reloadPageDetails(
            type = [],
            isJoinSocketPaymentInvoice = !IS_JOIN_SOCKET_PAYMENT_INVOICE
        ) {
            await this.getDetails(type, isJoinSocketPaymentInvoice);
        },
        async storeAudit() {
            const invoiceId = _.clone(this.invoiceId);
            const fullPath = _.clone(this.$router.currentRoute.fullPath);

            if (!invoiceId) {
                return;
            }

            await this._getSetting();
            await this._saveAudit({
                action: "view",
                path: "/billing/invoice/view." + invoiceId,
                reference_id: invoiceId,
                fullPath
            });
        },
        /////////////////
        // FLOW SAVING //
        /////////////////
        /**
         * @method savingCheckFormValid
         * @description called from item/index.vue and then call function checkFormIsValid in item/create-update.vue
         * @return {Promise}
         */
        savingCheckFormValid() {
            return this.$refs.invoiceEdit.checkFormIsValid();
        },
        /**
         * @method savingEditItem
         * @description called from item/index.vue and then call function proceedSave in item/create-update.vue with
         * boolean isUpdateUses
         * @return {void}
         */
        savingEditInvoice() {
            this.$refs.invoiceEdit.proceedSave();
        },
        /**
         * @method saved
         * @description called from item/create-update.vue means the item has been saved
         * @return {void}
         */
        saved(result) {
            this.$emit("saved", result);
            this.reloadPageDetails();
            if (result.isUpdateUses) {
                this.reloadTabList("tabUses");
            } else {
                this.resetTab();
            }
        },
        /**
         * @method resetTab
         * @description To reset the tab to price tier or the first tab each time open edit/cancel edit and after edit
         * WIP: will make this dynamic by accept index to open specific tab
         */
        resetTab() {
            this.keyComponent++;
            this.activeTabIndex = 0;
        },
        async getDetails(
            type = [],
            isJoinSocketPaymentInvoice = !IS_JOIN_SOCKET_PAYMENT_INVOICE
        ) {
            let self = this;
            self.isDataReloading = true;
            const encodedId = this.$route.params.id;
            const index = this.$route.params.index;

            if (encodedId || index) {
                this.$store.commit("billing/setDirty", false);
                let res = await this.$store.dispatch(
                    "billing/getInvoiceDetails",
                    {
                        invoiceId: index || encodedId,
                        index: index ? 1 : 0
                    }
                );

                if (res && res.code == 200) {
                    // CHECK IF THE INVOICE HAS BEEN DELETED
                    // redirect to invoice billing
                    if (res.resource.isactive == 0) {
                        self.$emit("redirect-to-dashboard");
                        return;
                    }
                    self.dynamicPageTitle(res.resource.name);
                    res.resource.isPaid =
                        (res.resource.balance == 0 &&
                            res.resource.status ==
                                CBILLINGS.INVOICE.TYPE_PAID) ||
                        res.resource.status ==
                            CBILLINGS.INVOICE.TYPE_WRITEOFF ||
                        res.resource.status == CBILLINGS.INVOICE.TYPE_CLOSED;

                    res.resource.isBusinessActorActive =
                        res.resource.client &&
                        res.resource.client.single_business_actor &&
                        res.resource.client.single_business_actor.isactive;

                    setTimeout(() => {
                        self.storeAudit();
                    }, 1000);

                    self.$emit("set-invoice-details", res.resource);
                    self.isDataReloading = false;
                    self.firstTimeLoaded = true;

                    this.$nextTick(() => {
                        this.reloadPopupNotes();
                        this.reloadTabCount(type);

                        if (isJoinSocketPaymentInvoice) {
                            // reset the state
                            self.joinPaymentInvoice(
                                res.resource.id,
                                joinPaymentInvoiceSocket.WITHAWAIT
                            );
                        }
                    });
                } else if (!this.isBusinessLoading) {
                    if (res.switch_business) {
                        //switch clinic/business when checking in PreventRestrictedUser middleware
                        this.$refs.modalSwitchBusiness.open(res);
                    } else if (res.no_access) {
                        //no access when checking in PreventRestrictedUser middleware
                        self._message(res.message, "danger");
                        self.redirectToDashboard();
                    } else {
                        self.redirectToDashboard();
                    }
                }
            } else {
                self.redirectToDashboard();
            }
        },
        reloadPopupNotes() {
            this.$store.commit("client/clearPopupNotes"); // clear popup notes by default
            this.$store.dispatch(
                "client/loadPopupNotes",
                this.invoiceDetails.client.id
            );
        },
        reloadTabCount(type = null) {
            InvoiceGeneralService.getTabCount({
                invoice_id: this.invoiceId,
                client_id: this.invoiceDetails.client.id,
                type: type
            }).then(response => {
                if (response) {
                    assignIn(this.tabCount, response);
                }
            });
        },
        openModal(ref) {
            switch (ref) {
                default:
                    this.$refs[ref].open();
            }
        },
        reloadTabList(tabName) {
            this.activeTabIndex = this.findTabIndex(tabName);
            this.keyComponent++;
        },
        async reloadPage(tabName) {
            await this.reloadPageDetails();
            if (tabName) {
                this.reloadTabList(tabName);
            }
        },
        findTabIndex(tabname) {
            return findIndex(this.$refs.btabs.$children, function (tab) {
                return tab.$attrs.tabname == tabname;
            });
        },
        createPayment() {
            this.$refs.createPaymentWindow.open();
        },
        createCredit() {
            this.$refs["modal-edit-credit"].open();
        },
        createBundle() {
            let date = this._fromDateCarbon(this.invoiceDetails.start).toFormat(
                "X"
            );

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs.modalBundle.open(),
                onError: message => this._message(message, "danger")
            });
        },
        addInvoiceItem() {
            this.$refs["invoiceItemModal"].open();
        },
        openVetsourceModal(params) {
            params.clientId = this.invoiceDetails.client.id;
            this.$refs["modalVetsource"].open(params);
        },
        redirectToDashboard() {
            this.$router.push({ name: "billing_dashboard" });
        }
    }
};
</script>

<style scoped>
.preloader {
    margin-left: 15px;
}
</style>
